import { ModelEntityType } from '@upright/common/model/input/types';
import { KeyTakeaway, KeyTakeawayType } from '@upright/common/model/output/scores/key-takeaways/types';
import { Valence } from '@upright/common/model/types';
import { safeAssert } from '@upright/common/safe-assert';
import { RequestStatus } from '@upright/ui/api/common/types';
import { LensBoxMode } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { useCarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { useNetImpactLensContext } from '@upright/ui/common/upright/core/impact-lenses/net-impact/common/use-net-impact-lens-context';
import { KeyTakeawayBoxFrame } from './key-takeaway-box-frame';
import { KeyTakeawayContent } from './key-takeaway-content';
import { TopImpactContent } from './top-impact';

interface Props {
  valence: Valence.NEGATIVE | Valence.POSITIVE;
}

export function KeyTakeawayInfoBox(props: Props) {
  const { valence } = props;
  const context = useNetImpactLensContext();
  const data = context?.data;
  const layout = useCarouselLayout();

  if (!data || !data.entity || layout.boxMode !== LensBoxMode.SIDE || data.loadingStatus === RequestStatus.LOADING) {
    return <></>;
  }

  const modelEntityType = data.entity.type;

  const disableLinks = !!context.options.disableLinks;

  const keyTakeawayContent = (() => {
    switch (modelEntityType) {
      case ModelEntityType.COMPANY: {
        const keyTakeaway = chooseTakeawayToShow(data.takeaways, valence);
        if (!keyTakeaway) {
          // Not every company has a biggest-positive/negative takeaway for this
          // valence — expected, and handled by rendering nothing. No warning (it
          // fired on every such render and was just log noise).
          return undefined;
        }

        if (!context.data.isLoadingProductNames && !data.productNames) {
          console.warn('No product data available for key takeaways.');
          return undefined;
        }
        safeAssert(keyTakeaway.entityType === ModelEntityType.COMPANY);
        return (
          <KeyTakeawayContent
            takeaway={keyTakeaway}
            entityType={modelEntityType}
            entityName={data.entity.name}
            productNames={data.productNames}
            hasProductViewAccess={data.hasProductViewAccess ?? false}
            disableLinks={disableLinks}
            isLoadingData={!!context.data.isLoadingProductNames}
          />
        );
      }
      case ModelEntityType.GROUP:
      case ModelEntityType.PORTFOLIO: {
        const topImpactData = data.topImpactData?.[props.valence];

        return (
          <TopImpactContent
            entityType={modelEntityType}
            valence={props.valence}
            largest={topImpactData?.largestImpact}
            mostDistinct={topImpactData?.mostDistinctImpact}
            disableLinks={disableLinks}
            accessLevel={data.accessLevel}
            source={data.source}
          />
        );
      }
      case ModelEntityType.PRODUCT:
      case ModelEntityType.MIXIN:
      case ModelEntityType.PHENOMENON:
      default:
        throw Error(`Takeaways not supported for the entity type: ${modelEntityType}`);
    }
  })();

  if (!keyTakeawayContent) {
    return <></>;
  }

  return (
    <KeyTakeawayBoxFrame valence={props.valence} entityName={data.entity.name}>
      {keyTakeawayContent}
    </KeyTakeawayBoxFrame>
  );
}

function chooseTakeawayToShow(takeaways: KeyTakeaway[] | undefined, valence: Valence.NEGATIVE | Valence.POSITIVE) {
  return takeaways?.find(
    d =>
      (d.type === KeyTakeawayType.BIGGEST_POSITIVE && valence === Valence.POSITIVE) ||
      (d.type === KeyTakeawayType.BIGGEST_NEGATIVE && valence === Valence.NEGATIVE),
  );
}
